import React, { useEffect, useState } from 'react';
import { 
  Flower2, Users, Building2, TrendingUp, Activity, 
  Heart, Pill, BookOpen, Award, AlertCircle, Check,
  X, Clock, Eye, Download, RefreshCw, Shield, Bell, Send,
  Calendar, MapPin, MessageCircle, Trash2, Edit2, Plus,
  FileSpreadsheet, FileText, Lightbulb, Globe
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import UserDirectory from '../components/UserDirectory';
import InstitutionalReport from '../components/InstitutionalReport';
import InteroperabilityHub from '../components/InteroperabilityHub';

const CITIES = ["Abidjan", "Bouaké", "Yamoussoukro", "Daloa", "San-Pédro", "Korhogo", "Man", "Gagnoa", "Divo", "Abengourou"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [cycleMetrics, setCycleMetrics] = useState(null);
  const [contraceptionMetrics, setContraceptionMetrics] = useState(null);
  const [engagementMetrics, setEngagementMetrics] = useState(null);
  const [partners, setPartners] = useState({ applications: [], counts: {} });
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [subscriptionPlan, setSubscriptionPlan] = useState('basic');
  
  // Girls Room state
  const [girlsRoomEvents, setGirlsRoomEvents] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    date: '', time: '', location: '', theme: '', animator: '', city: 'Abidjan', description: ''
  });
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ title: '', message: '', city: 'all', eventId: null });
  
  // Community state
  const [pendingQuestions, setPendingQuestions] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  // State for advanced metrics
  const [geoMetrics, setGeoMetrics] = useState(null);
  const [educationMetrics, setEducationMetrics] = useState(null);
  const [healthBehavior, setHealthBehavior] = useState(null);
  const [communityMetrics, setCommunityMetrics] = useState(null);
  const [retentionMetrics, setRetentionMetrics] = useState(null);
  const [commercialSummary, setCommercialSummary] = useState(null);
  const [activeMetricTab, setActiveMetricTab] = useState('overview');

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashRes, demoRes, cycleRes, contraRes, engageRes, partnerRes, eventsRes, questionsRes] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/metrics/demographics'),
        api.get('/admin/metrics/cycle'),
        api.get('/admin/metrics/contraception'),
        api.get('/admin/metrics/engagement'),
        api.get('/admin/partners'),
        api.get('/admin/girls-room/events').catch(() => ({ data: { events: [] } })),
        api.get('/admin/community/pending').catch(() => ({ data: { questions: [] } }))
      ]);

      setDashboard(dashRes.data);
      setDemographics(demoRes.data);
      setCycleMetrics(cycleRes.data);
      setContraceptionMetrics(contraRes.data);
      setEngagementMetrics(engageRes.data);
      setPartners(partnerRes.data);
      setGirlsRoomEvents(eventsRes.data.events || []);
      setPendingQuestions(questionsRes.data.questions || []);
      
      // Fetch advanced metrics in background
      fetchAdvancedMetrics();
    } catch (error) {
      if (error.response?.status === 403) {
        toast({
          title: 'Accès refusé',
          description: 'Vous n\'avez pas les droits Super Admin',
          variant: 'destructive'
        });
        navigate('/dashboard');
      } else {
        toast({
          title: 'Erreur',
          description: 'Impossible de charger les données admin',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvancedMetrics = async () => {
    try {
      const [geoRes, eduRes, healthRes, commRes, retRes, summaryRes] = await Promise.all([
        api.get('/admin/metrics/geographic').catch(() => null),
        api.get('/admin/metrics/education-impact').catch(() => null),
        api.get('/admin/metrics/health-behavior').catch(() => null),
        api.get('/admin/metrics/community-engagement').catch(() => null),
        api.get('/admin/metrics/retention-cohorts').catch(() => null),
        api.get('/admin/metrics/commercial-summary').catch(() => null)
      ]);
      
      if (geoRes) setGeoMetrics(geoRes.data);
      if (eduRes) setEducationMetrics(eduRes.data);
      if (healthRes) setHealthBehavior(healthRes.data);
      if (commRes) setCommunityMetrics(commRes.data);
      if (retRes) setRetentionMetrics(retRes.data);
      if (summaryRes) setCommercialSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching advanced metrics:', error);
    }
  };

  // Girls Room functions
  const handleCreateEvent = async () => {
    try {
      await api.post('/admin/girls-room/events', eventForm);
      toast({ title: 'Événement créé!' });
      setShowEventDialog(false);
      setEventForm({ date: '', time: '', location: '', theme: '', animator: '', city: 'Abidjan', description: '' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleUpdateEvent = async () => {
    try {
      await api.put(`/admin/girls-room/events/${editingEvent.id}`, eventForm);
      toast({ title: 'Événement mis à jour!' });
      setEditingEvent(null);
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Supprimer cet événement?')) return;
    try {
      await api.delete(`/admin/girls-room/events/${eventId}`);
      toast({ title: 'Événement supprimé' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleSendNotification = async () => {
    try {
      const res = await api.post('/admin/girls-room/notify', notifyForm);
      toast({ title: 'Notification envoyée!', description: res.data.message });
      setShowNotifyDialog(false);
      setNotifyForm({ title: '', message: '', city: 'all', eventId: null });
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  const openNotifyForEvent = (event) => {
    setNotifyForm({
      title: `🌸 Session Girls Room: ${event.theme}`,
      message: `${event.date} à ${event.time} - ${event.location}, ${event.city}. Animée par ${event.animator}. Inscris-toi dans l'app!`,
      city: event.city,
      eventId: event.id
    });
    setShowNotifyDialog(true);
  };

  // Community moderation
  const handleModerateQuestion = async (questionId, action) => {
    try {
      await api.put(`/admin/community/questions/${questionId}/moderate`, { action });
      toast({ title: action === 'approve' ? 'Question approuvée' : 'Question rejetée' });
      fetchAllData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  // Export functions
  const handleExportExcel = async () => {
    try {
      toast({ title: 'Export en cours...', description: 'Génération du fichier Excel' });
      const response = await api.get('/admin/export/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fleur_de_vie_metrics_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'Export réussi!', description: 'Fichier Excel téléchargé' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'exporter les données', variant: 'destructive' });
    }
  };

  const handleExportPDF = async () => {
    try {
      toast({ title: 'Export en cours...', description: 'Génération du rapport PDF' });
      const response = await api.get('/admin/export/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fleur_de_vie_rapport_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: 'Export réussi!', description: 'Rapport PDF téléchargé' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de générer le rapport', variant: 'destructive' });
    }
  };

  const handlePartnerAction = async (partnerId, action) => {
    try {
      let endpoint = `/admin/partners/${partnerId}/${action}`;
      let data = {};

      if (action === 'validate') {
        data = { subscriptionPlan };
      } else if (action === 'suspend' || action === 'reject') {
        data = { reason: actionReason };
      }

      await api.post(endpoint, data);

      toast({
        title: 'Succès',
        description: `Partenaire ${action === 'validate' ? 'validé' : action === 'suspend' ? 'suspendu' : action === 'reject' ? 'rejeté' : 'réactivé'}`
      });

      setSelectedPartner(null);
      setActionReason('');
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Action échouée',
        variant: 'destructive'
      });
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce partenaire?')) {
      return;
    }

    try {
      await api.delete(`/admin/partners/${partnerId}`);
      toast({ title: 'Partenaire supprimé' });
      setSelectedPartner(null);
      fetchAllData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Suppression échouée',
        variant: 'destructive'
      });
    }
  };

  const exportMetrics = async () => {
    try {
      const response = await api.get('/admin/metrics/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fleurdevie_metrics_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast({ title: 'Export téléchargé' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Export échoué',
        variant: 'destructive'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'validated': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'suspended': return 'bg-orange-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      government: 'Gouvernement',
      ngo: 'ONG',
      international_org: 'Organisation Internationale',
      pharmaceutical: 'Laboratoire Pharmaceutique',
      ministry: 'Ministère de la Santé'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement du tableau de bord admin...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-8">
      {/* Header */}
      <div className="sticky top-0 bg-gray-900/95 backdrop-blur-md border-b border-gray-700 px-6 py-4 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Super Admin</h1>
              <p className="text-gray-400 text-sm">Tableau de bord</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/admin/tips')} variant="outline" size="sm" className="text-yellow-300 border-yellow-600 hover:bg-yellow-600/20">
              <Lightbulb className="w-4 h-4 mr-2" />
              Astuces
            </Button>
            <Button onClick={fetchAllData} variant="outline" size="sm" className="text-gray-300 border-gray-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button onClick={handleExportExcel} variant="outline" size="sm" className="text-green-300 border-green-600 hover:bg-green-600/20">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button onClick={handleExportPDF} className="bg-gradient-to-r from-purple-600 to-pink-600">
              <FileText className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Card className="p-5 bg-gradient-to-br from-blue-600 to-blue-800 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-200 text-sm">Utilisatrices</p>
                <p className="text-white text-2xl font-bold">{dashboard?.overview?.totalUsers || 0}</p>
                <p className="text-blue-200 text-xs">+{dashboard?.overview?.newUsersThisMonth || 0} ce mois ({dashboard?.overview?.growthRate || 0}%)</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-green-600 to-emerald-800 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-green-200 text-sm">Actives</p>
                <p className="text-white text-2xl font-bold">{dashboard?.overview?.activeUsers7d || dashboard?.overview?.activeUsers || 0}</p>
                <p className="text-green-200 text-xs">7j: {dashboard?.overview?.retention7d || 0}% | 30j: {dashboard?.overview?.retention30d || 0}%</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-purple-600 to-purple-800 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-purple-200 text-sm">Partenaires</p>
                <p className="text-white text-2xl font-bold">{partners?.counts?.validated || dashboard?.partners?.total || 0}</p>
                <p className="text-purple-200 text-xs">{partners?.counts?.pending || dashboard?.partners?.pending || 0} en attente</p>
              </div>
            </div>
          </Card>

          <Card className="p-5 bg-gradient-to-br from-pink-600 to-rose-800 border-0">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-pink-200 text-sm">Suivi Sante</p>
                <p className="text-white text-2xl font-bold">{dashboard?.engagement?.cycleTracking || 0}</p>
                <p className="text-pink-200 text-xs">{dashboard?.engagement?.contraceptionTracking || 0} contraception | {dashboard?.engagement?.pillLogsTotal || 0} logs pilule</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Platform & Engagement Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Platform Distribution */}
          <Card className="p-5 bg-gradient-to-br from-cyan-600 to-blue-800 border-0">
            <p className="text-cyan-200 text-sm mb-3 font-semibold">Plateformes</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Android (Google Play)</span>
                <span className="text-cyan-200 font-bold">{dashboard?.platforms?.android?.uniqueUsers || 0} users</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Web (PWA)</span>
                <span className="text-cyan-200 font-bold">{dashboard?.platforms?.web?.uniqueUsers || 0} users</span>
              </div>
              <div className="flex items-center justify-between mt-1 pt-1 border-t border-white/20">
                <span className="text-white text-sm">Inscription Email</span>
                <span className="text-cyan-200">{dashboard?.overview?.emailUsers || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Inscription Tel</span>
                <span className="text-cyan-200">{dashboard?.overview?.phoneUsers || 0}</span>
              </div>
            </div>
          </Card>

          {/* Education */}
          <Card className="p-5 bg-gradient-to-br from-yellow-600 to-orange-800 border-0">
            <p className="text-yellow-200 text-sm mb-3 font-semibold">Education</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Modules termines</span>
                <span className="text-yellow-200 font-bold">{dashboard?.engagement?.modulesCompleted || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">En cours</span>
                <span className="text-yellow-200">{dashboard?.engagement?.modulesInProgress || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Quiz reussis</span>
                <span className="text-yellow-200 font-bold">{dashboard?.engagement?.quizzesPassed || 0}/{dashboard?.engagement?.quizAttempts || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Score moyen</span>
                <span className="text-yellow-200">{dashboard?.engagement?.avgQuizScore || 0}%</span>
              </div>
            </div>
          </Card>

          {/* Gamification & Notifications */}
          <Card className="p-5 bg-gradient-to-br from-amber-600 to-red-800 border-0">
            <p className="text-amber-200 text-sm mb-3 font-semibold">Gamification & Notifs</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Points total</span>
                <span className="text-amber-200 font-bold">{dashboard?.gamification?.totalPointsEarned || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Points moyen</span>
                <span className="text-amber-200">{dashboard?.gamification?.avgPoints || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Niveaux</span>
                <span className="text-amber-200">{(dashboard?.gamification?.levelDistribution || []).map(l => `Niv${l.level}:${l.users}`).join(' ')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white text-sm">Alertes envoyees</span>
                <span className="text-amber-200">{dashboard?.notifications?.alertsSentToday || 0} aujourd'hui</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="metrics" className="space-y-6">
          <TabsList className="bg-gray-800 border-gray-700 flex-wrap">
            <TabsTrigger value="metrics" className="data-[state=active]:bg-purple-600">
              <TrendingUp className="w-4 h-4 mr-2" />
              Metriques
            </TabsTrigger>
            <TabsTrigger value="institutional" className="data-[state=active]:bg-cyan-600">
              <Building2 className="w-4 h-4 mr-2" />
              Rapport Institutionnel
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-pink-600">
              <Users className="w-4 h-4 mr-2" />
              Repertoire
            </TabsTrigger>
            <TabsTrigger value="girlsroom" className="data-[state=active]:bg-purple-600">
              <Users className="w-4 h-4 mr-2" />
              Girls Rooms ({girlsRoomEvents.length})
            </TabsTrigger>
            <TabsTrigger value="community" className="data-[state=active]:bg-purple-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              Communaute ({pendingQuestions.length})
            </TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-purple-600">
              <Building2 className="w-4 h-4 mr-2" />
              Partenaires ({partners?.counts?.pending || 0})
            </TabsTrigger>
            <TabsTrigger value="advanced" className="data-[state=active]:bg-purple-600">
              <TrendingUp className="w-4 h-4 mr-2" />
              Avance
            </TabsTrigger>
            <TabsTrigger value="interoperability" className="data-[state=active]:bg-cyan-600" data-testid="tab-interoperability">
              <Globe className="w-4 h-4 mr-2" />
              Interoperabilite
            </TabsTrigger>
          </TabsList>

          {/* Metrics Tab */}

          {/* Institutional Report Tab */}
          <TabsContent value="institutional" className="space-y-6">
            <InstitutionalReport />
          </TabsContent>

          {/* User Directory Tab */}
          <TabsContent value="users" className="space-y-6">
            <UserDirectory />
          </TabsContent>

          <TabsContent value="metrics" className="space-y-6">
            {/* Demographics */}
            <Card className="p-6 bg-gray-800 border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-bold text-white">Données Démographiques</h3>
                <span className="ml-auto text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                  Valeur: Gouvernements, ONG
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Quotidien (DAU)</p>
                  <p className="text-white text-xl font-bold">{demographics?.activeUsers?.daily || 0}</p>
                </div>
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Hebdo (WAU)</p>
                  <p className="text-white text-xl font-bold">{demographics?.activeUsers?.weekly || 0}</p>
                </div>
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Mensuel (MAU)</p>
                  <p className="text-white text-xl font-bold">{demographics?.activeUsers?.monthly || 0}</p>
                </div>
                <div className="p-4 bg-gray-700/50 rounded-lg">
                  <p className="text-gray-400 text-sm">Rétention</p>
                  <p className="text-white text-xl font-bold">{demographics?.retentionRate || 0}%</p>
                </div>
              </div>
            </Card>

            {/* Cycle Metrics */}
            <Card className="p-6 bg-gray-800 border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <Heart className="w-5 h-5 text-pink-400" />
                <h3 className="text-lg font-bold text-white">Métriques Cycle Menstruel</h3>
                <span className="ml-auto text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                  Valeur: OMS, Ministères Santé
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="p-4 bg-pink-900/30 rounded-lg">
                  <p className="text-pink-300 text-sm">Utilisatrices</p>
                  <p className="text-white text-xl font-bold">{cycleMetrics?.usersTracking || 0}</p>
                </div>
                <div className="p-4 bg-pink-900/30 rounded-lg">
                  <p className="text-pink-300 text-sm">Cycle moyen</p>
                  <p className="text-white text-xl font-bold">{cycleMetrics?.averages?.cycleLength || 28} jours</p>
                </div>
                <div className="p-4 bg-pink-900/30 rounded-lg">
                  <p className="text-pink-300 text-sm">Règles moyennes</p>
                  <p className="text-white text-xl font-bold">{cycleMetrics?.averages?.periodLength || 5} jours</p>
                </div>
                <div className="p-4 bg-pink-900/30 rounded-lg">
                  <p className="text-pink-300 text-sm">Régularité</p>
                  <p className="text-white text-xl font-bold">{cycleMetrics?.regularity?.regularityRate || 0}%</p>
                </div>
              </div>
            </Card>

            {/* Contraception Metrics */}
            <Card className="p-6 bg-gray-800 border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <Pill className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg font-bold text-white">Métriques Contraception</h3>
                <span className="ml-auto text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                  Valeur: Pharmaceutiques, Planification Familiale
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                <div className="p-4 bg-purple-900/30 rounded-lg">
                  <p className="text-purple-300 text-sm">Utilisatrices</p>
                  <p className="text-white text-xl font-bold">{contraceptionMetrics?.usersTracking || 0}</p>
                </div>
                <div className="p-4 bg-purple-900/30 rounded-lg">
                  <p className="text-purple-300 text-sm">Observance moyenne</p>
                  <p className="text-white text-xl font-bold">{contraceptionMetrics?.adherence?.averageRate || 0}%</p>
                </div>
                <div className="p-4 bg-purple-900/30 rounded-lg">
                  <p className="text-purple-300 text-sm">Total prises enregistrées</p>
                  <p className="text-white text-xl font-bold">{contraceptionMetrics?.adherence?.totalLogsRecorded || 0}</p>
                </div>
              </div>
              {contraceptionMetrics?.methodDistribution && (
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm">Distribution des méthodes:</p>
                  {Object.entries(contraceptionMetrics.methodDistribution).map(([method, data]) => (
                    <div key={method} className="flex items-center gap-3">
                      <span className="text-white text-sm w-32">{method}</span>
                      <Progress value={data.percentage} className="flex-1 h-2" />
                      <span className="text-gray-400 text-sm w-16">{data.percentage}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Engagement Metrics */}
            <Card className="p-6 bg-gray-800 border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-bold text-white">Métriques Engagement</h3>
                <span className="ml-auto text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                  Valeur: ONG Éducation, Bailleurs
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-green-900/30 rounded-lg">
                  <p className="text-green-300 text-sm">Modules terminés</p>
                  <p className="text-white text-xl font-bold">{engagementMetrics?.modules?.completed || 0}</p>
                </div>
                <div className="p-4 bg-green-900/30 rounded-lg">
                  <p className="text-green-300 text-sm">Quiz réussis</p>
                  <p className="text-white text-xl font-bold">{engagementMetrics?.quizzes?.passed || 0}</p>
                </div>
                <div className="p-4 bg-green-900/30 rounded-lg">
                  <p className="text-green-300 text-sm">Taux réussite quiz</p>
                  <p className="text-white text-xl font-bold">{engagementMetrics?.quizzes?.passRate || 0}%</p>
                </div>
                <div className="p-4 bg-green-900/30 rounded-lg">
                  <p className="text-green-300 text-sm">Points distribués</p>
                  <p className="text-white text-xl font-bold">{engagementMetrics?.gamification?.totalPointsEarned || 0}</p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Girls Room Tab */}
          <TabsContent value="girlsroom" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Sessions Girls Rooms</h3>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowNotifyDialog(true)}
                  variant="outline"
                  className="border-pink-500 text-pink-300 hover:bg-pink-500/20"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifier tous
                </Button>
                <Button 
                  onClick={() => setShowEventDialog(true)}
                  className="bg-pink-500 hover:bg-pink-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle session
                </Button>
              </div>
            </div>

            {girlsRoomEvents.length === 0 ? (
              <Card className="p-12 bg-gray-800 border-gray-700 text-center">
                <Users className="w-12 h-12 text-pink-300 mx-auto mb-3 opacity-50" />
                <p className="text-gray-400">Aucune session planifiée</p>
                <Button onClick={() => setShowEventDialog(true)} className="mt-4 bg-pink-500">
                  Créer la première session
                </Button>
              </Card>
            ) : (
              <div className="grid gap-4">
                {girlsRoomEvents.map((event) => (
                  <Card key={event.id} className="p-4 bg-gray-800 border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-pink-500/20 text-pink-300 text-xs rounded">
                            {event.city}
                          </span>
                          <span className="text-gray-400 text-xs">
                            {event.registrationCount || 0} inscrites
                          </span>
                        </div>
                        <h4 className="text-white font-semibold">{event.theme}</h4>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {event.date} à {event.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {event.location}
                          </span>
                          <span>Animée par {event.animator}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openNotifyForEvent(event)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingEvent(event);
                            setEventForm({
                              date: event.date, time: event.time, location: event.location,
                              theme: event.theme, animator: event.animator, city: event.city,
                              description: event.description || ''
                            });
                          }}
                          size="sm"
                          variant="outline"
                          className="border-gray-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteEvent(event.id)}
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-500/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Community Moderation Tab */}
          <TabsContent value="community" className="space-y-6">
            <h3 className="text-lg font-bold text-white">Questions en attente de modération</h3>
            
            {pendingQuestions.length === 0 ? (
              <Card className="p-12 bg-gray-800 border-gray-700 text-center">
                <MessageCircle className="w-12 h-12 text-purple-300 mx-auto mb-3 opacity-50" />
                <p className="text-gray-400">Aucune question en attente</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingQuestions.map((question) => (
                  <Card key={question.id} className="p-4 bg-gray-800 border-gray-700">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-purple-300 text-xs">#{question.anonymousId}</span>
                          <span className="px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded">
                            {question.category}
                          </span>
                        </div>
                        <p className="text-white">{question.content}</p>
                        <p className="text-gray-500 text-xs mt-2">
                          {new Date(question.createdAt).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleModerateQuestion(question.id, 'approve')}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approuver
                        </Button>
                        <Button
                          onClick={() => handleModerateQuestion(question.id, 'reject')}
                          size="sm"
                          variant="outline"
                          className="border-red-600 text-red-400 hover:bg-red-500/20"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Rejeter
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-6">
            {/* Partner Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-yellow-900/30 border-yellow-700/50">
                <p className="text-yellow-300 text-sm">En attente</p>
                <p className="text-white text-2xl font-bold">{partners?.counts?.pending || 0}</p>
              </Card>
              <Card className="p-4 bg-green-900/30 border-green-700/50">
                <p className="text-green-300 text-sm">Validés</p>
                <p className="text-white text-2xl font-bold">{partners?.counts?.validated || 0}</p>
              </Card>
              <Card className="p-4 bg-orange-900/30 border-orange-700/50">
                <p className="text-orange-300 text-sm">Suspendus</p>
                <p className="text-white text-2xl font-bold">{partners?.counts?.suspended || 0}</p>
              </Card>
              <Card className="p-4 bg-red-900/30 border-red-700/50">
                <p className="text-red-300 text-sm">Rejetés</p>
                <p className="text-white text-2xl font-bold">{partners?.counts?.rejected || 0}</p>
              </Card>
            </div>

            {/* Partners List */}
            <Card className="p-6 bg-gray-800 border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4">Demandes de Partenariat</h3>
              
              {partners?.applications?.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune demande de partenariat</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {partners.applications.map((partner) => (
                    <div 
                      key={partner.id}
                      className="p-4 bg-gray-700/50 rounded-lg flex items-center justify-between hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-3 h-3 rounded-full ${getStatusColor(partner.status)}`} />
                        <div>
                          <p className="text-white font-medium">{partner.organizationName}</p>
                          <p className="text-gray-400 text-sm">
                            {getTypeLabel(partner.organizationType)} • {partner.country}
                          </p>
                          <p className="text-gray-500 text-xs">{partner.contactEmail}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          partner.status === 'validated' ? 'bg-green-500/20 text-green-400' :
                          partner.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          partner.status === 'suspended' ? 'bg-orange-500/20 text-orange-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {partner.status}
                        </span>
                        
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setSelectedPartner(partner)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl max-h-[80vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="text-white">{partner.organizationName}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-gray-400">Type</Label>
                                  <p className="text-white">{getTypeLabel(partner.organizationType)}</p>
                                </div>
                                <div>
                                  <Label className="text-gray-400">Pays</Label>
                                  <p className="text-white">{partner.country}</p>
                                </div>
                                <div>
                                  <Label className="text-gray-400">Contact</Label>
                                  <p className="text-white">{partner.contactName}</p>
                                </div>
                                <div>
                                  <Label className="text-gray-400">Email</Label>
                                  <p className="text-white">{partner.contactEmail}</p>
                                </div>
                              </div>
                              
                              <div>
                                <Label className="text-gray-400">Description</Label>
                                <p className="text-white text-sm">{partner.description}</p>
                              </div>
                              
                              <div>
                                <Label className="text-gray-400">Mission</Label>
                                <p className="text-white text-sm">{partner.mission}</p>
                              </div>
                              
                              <div>
                                <Label className="text-gray-400">Objectifs du partenariat</Label>
                                <p className="text-white text-sm">{partner.partnershipGoals}</p>
                              </div>
                              
                              <div>
                                <Label className="text-gray-400">Usage prévu des données</Label>
                                <p className="text-white text-sm">{partner.expectedDataUsage}</p>
                              </div>
                              
                              {partner.status === 'pending' && (
                                <div className="space-y-3 pt-4 border-t border-gray-700">
                                  <Label className="text-gray-400">Plan d'abonnement</Label>
                                  <Select value={subscriptionPlan} onValueChange={setSubscriptionPlan}>
                                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-gray-700 border-gray-600">
                                      <SelectItem value="basic">Basic (500€/mois)</SelectItem>
                                      <SelectItem value="pro">Pro (1500€/mois)</SelectItem>
                                      <SelectItem value="enterprise">Enterprise (5000€/mois)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  
                                  <div className="flex gap-2">
                                    <Button 
                                      onClick={() => handlePartnerAction(partner.id, 'validate')}
                                      className="flex-1 bg-green-600 hover:bg-green-700"
                                    >
                                      <Check className="w-4 h-4 mr-2" />
                                      Valider
                                    </Button>
                                    <Button 
                                      onClick={() => {
                                        const reason = prompt('Raison du rejet:');
                                        if (reason) {
                                          setActionReason(reason);
                                          handlePartnerAction(partner.id, 'reject');
                                        }
                                      }}
                                      variant="destructive"
                                      className="flex-1"
                                    >
                                      <X className="w-4 h-4 mr-2" />
                                      Rejeter
                                    </Button>
                                  </div>
                                </div>
                              )}
                              
                              {partner.status === 'validated' && (
                                <div className="flex gap-2 pt-4 border-t border-gray-700">
                                  <Button 
                                    onClick={() => {
                                      const reason = prompt('Raison de la suspension:');
                                      if (reason) {
                                        setActionReason(reason);
                                        handlePartnerAction(partner.id, 'suspend');
                                      }
                                    }}
                                    variant="outline"
                                    className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-500/20"
                                  >
                                    Suspendre
                                  </Button>
                                </div>
                              )}
                              
                              {partner.status === 'suspended' && (
                                <div className="flex gap-2 pt-4 border-t border-gray-700">
                                  <Button 
                                    onClick={() => handlePartnerAction(partner.id, 'reactivate')}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                  >
                                    Réactiver
                                  </Button>
                                  <Button 
                                    onClick={() => handleDeletePartner(partner.id)}
                                    variant="destructive"
                                    className="flex-1"
                                  >
                                    Supprimer
                                  </Button>
                                </div>
                              )}
                              
                              {partner.status === 'rejected' && (
                                <div className="pt-4 border-t border-gray-700">
                                  <p className="text-red-400 text-sm mb-3">
                                    Raison: {partner.rejectionReason || 'Non spécifiée'}
                                  </p>
                                  <Button 
                                    onClick={() => handleDeletePartner(partner.id)}
                                    variant="destructive"
                                    className="w-full"
                                  >
                                    Supprimer définitivement
                                  </Button>
                                </div>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Advanced Metrics Tab */}
          <TabsContent value="advanced" className="space-y-6">
            {/* Metric Sub-tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
                { id: 'retention', label: 'Rétention', icon: '📈' },
                { id: 'geographic', label: 'Géographie', icon: '🗺️' },
                { id: 'education', label: 'Éducation', icon: '📚' },
                { id: 'health', label: 'Santé', icon: '💊' },
                { id: 'community', label: 'Communauté', icon: '💬' }
              ].map(tab => (
                <Button
                  key={tab.id}
                  variant={activeMetricTab === tab.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveMetricTab(tab.id)}
                  className={activeMetricTab === tab.id ? 'bg-purple-600' : 'border-gray-600 text-gray-300'}
                >
                  {tab.icon} {tab.label}
                </Button>
              ))}
            </div>

            {/* Commercial Overview */}
            {activeMetricTab === 'overview' && commercialSummary && (
              <div className="space-y-6">
                <Card className="p-6 bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/30">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {commercialSummary.executiveSummary?.title}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Données en temps réel • Générées le {new Date(commercialSummary.executiveSummary?.generatedAt).toLocaleDateString('fr-FR')}
                  </p>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="p-5 bg-blue-900/30 border-blue-500/30">
                    <p className="text-blue-300 text-sm">Utilisatrices totales</p>
                    <p className="text-white text-3xl font-bold">{commercialSummary.reachAndEngagement?.totalUsers || 0}</p>
                    <p className="text-blue-300 text-xs mt-1">{commercialSummary.reachAndEngagement?.targetDemographic}</p>
                  </Card>
                  <Card className="p-5 bg-green-900/30 border-green-500/30">
                    <p className="text-green-300 text-sm">Actives mensuelles (MAU)</p>
                    <p className="text-white text-3xl font-bold">{commercialSummary.reachAndEngagement?.monthlyActiveUsers || 0}</p>
                    <p className="text-green-300 text-xs mt-1">{commercialSummary.reachAndEngagement?.engagementRate}% taux d'engagement</p>
                  </Card>
                  <Card className="p-5 bg-pink-900/30 border-pink-500/30">
                    <p className="text-pink-300 text-sm">Suivi santé</p>
                    <p className="text-white text-3xl font-bold">{commercialSummary.healthImpact?.healthTrackingAdoption || 0}%</p>
                    <p className="text-pink-300 text-xs mt-1">Adoption cycle + contraception</p>
                  </Card>
                  <Card className="p-5 bg-purple-900/30 border-purple-500/30">
                    <p className="text-purple-300 text-sm">Quiz réussis</p>
                    <p className="text-white text-3xl font-bold">{commercialSummary.educationalImpact?.quizzesPassed || 0}</p>
                    <p className="text-purple-300 text-xs mt-1">{commercialSummary.educationalImpact?.modulesCompleted || 0} modules complétés</p>
                  </Card>
                </div>

                {/* Partnership Opportunities */}
                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Opportunités de partenariat</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {commercialSummary.partnershipOpportunities?.partnershipTypes?.map((p, idx) => (
                      <div key={idx} className="p-4 bg-gray-700/50 rounded-lg">
                        <p className="text-white font-medium">{p.type}</p>
                        <p className="text-gray-400 text-sm">{p.benefit}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Data Monetization */}
                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Potentiel de monétisation des données</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(commercialSummary.dataMonetizationPotential || {}).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <p className="text-gray-400 text-xs uppercase">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                        <p className="text-white text-sm mt-1">{value}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* Retention Cohorts */}
            {activeMetricTab === 'retention' && retentionMetrics && (
              <div className="space-y-6">
                <Card className="p-6 bg-gray-800 border-gray-700">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-bold text-white">Rétention par cohortes</h3>
                    <span className="ml-auto text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                      Valeur: Growth teams, Investisseurs
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-gray-400 text-sm border-b border-gray-700">
                          <th className="text-left py-3 px-2">Cohorte</th>
                          <th className="text-right py-3 px-2">Utilisatrices</th>
                          <th className="text-right py-3 px-2">Actives</th>
                          <th className="text-right py-3 px-2">Rétention</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retentionMetrics.cohortRetention?.map((cohort, idx) => (
                          <tr key={idx} className="text-white border-b border-gray-700/50">
                            <td className="py-3 px-2">{cohort.cohort}</td>
                            <td className="text-right py-3 px-2">{cohort.totalUsers}</td>
                            <td className="text-right py-3 px-2">{cohort.stillActive}</td>
                            <td className="text-right py-3 px-2">
                              <span className={`px-2 py-1 rounded text-sm ${
                                cohort.retentionRate > 50 ? 'bg-green-500/20 text-green-400' :
                                cohort.retentionRate > 25 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {cohort.retentionRate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Analyse du churn</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Utilisatrices totales</span>
                        <span className="text-white font-bold">{retentionMetrics.churnAnalysis?.totalUsers || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Churned (30j+)</span>
                        <span className="text-red-400 font-bold">{retentionMetrics.churnAnalysis?.churnedUsers || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Taux de churn</span>
                        <span className="text-red-400 font-bold">{retentionMetrics.churnAnalysis?.churnRate || 0}%</span>
                      </div>
                      <Progress 
                        value={100 - (retentionMetrics.churnAnalysis?.churnRate || 0)} 
                        className="h-3"
                      />
                      <p className="text-green-400 text-sm text-center">
                        {retentionMetrics.churnAnalysis?.activeUsers || 0} utilisatrices actives
                      </p>
                    </div>
                  </Card>

                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Distribution des streaks</h4>
                    <div className="space-y-3">
                      {Object.entries(retentionMetrics.streakDistribution || {}).map(([streak, count]) => (
                        <div key={streak} className="flex items-center gap-3">
                          <span className="text-gray-400 text-sm w-24">{streak} jours</span>
                          <div className="flex-1 bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{ width: `${Math.min((count / (retentionMetrics.churnAnalysis?.totalUsers || 1)) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="text-white text-sm w-12 text-right">{count}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* Geographic Metrics */}
            {activeMetricTab === 'geographic' && geoMetrics && (
              <div className="space-y-6">
                <Card className="p-6 bg-gray-800 border-gray-700">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-green-400" />
                    <h3 className="text-lg font-bold text-white">Distribution géographique</h3>
                    <span className="ml-auto text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                      Valeur: Ciblage régional
                    </span>
                  </div>
                  
                  <h4 className="text-white font-medium mb-3">Par ville</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    {Object.entries(geoMetrics.cityDistribution || {}).map(([city, count]) => (
                      <div key={city} className="p-3 bg-gray-700/50 rounded-lg text-center">
                        <p className="text-white font-bold text-lg">{count}</p>
                        <p className="text-gray-400 text-sm">{city}</p>
                      </div>
                    ))}
                  </div>

                  {geoMetrics.topSchools?.length > 0 && (
                    <>
                      <h4 className="text-white font-medium mb-3">Top établissements</h4>
                      <div className="space-y-2">
                        {geoMetrics.topSchools.slice(0, 5).map((school, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-2 bg-gray-700/30 rounded">
                            <span className="text-purple-400 font-bold w-6">#{idx + 1}</span>
                            <span className="text-white flex-1">{school.school}</span>
                            <span className="text-gray-400">{school.users} utilisatrices</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </Card>

                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Girls Room par ville</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(geoMetrics.girlsRoomByCity || {}).map(([city, count]) => (
                      <div key={city} className="p-3 bg-pink-900/30 rounded-lg text-center">
                        <p className="text-pink-300 font-bold text-lg">{count}</p>
                        <p className="text-gray-400 text-sm">{city}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-400 text-sm mt-4 text-center">
                    Total participations: {geoMetrics.totalGirlsRoomParticipations || 0}
                  </p>
                </Card>
              </div>
            )}

            {/* Education Impact */}
            {activeMetricTab === 'education' && educationMetrics && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="p-5 bg-blue-900/30 border-blue-500/30">
                    <p className="text-blue-300 text-sm">Apprenantes</p>
                    <p className="text-white text-2xl font-bold">{educationMetrics.overview?.totalLearners || 0}</p>
                  </Card>
                  <Card className="p-5 bg-green-900/30 border-green-500/30">
                    <p className="text-green-300 text-sm">Heures d'apprentissage</p>
                    <p className="text-white text-2xl font-bold">{educationMetrics.overview?.estimatedLearningHours || 0}h</p>
                  </Card>
                  <Card className="p-5 bg-purple-900/30 border-purple-500/30">
                    <p className="text-purple-300 text-sm">Leçons complétées</p>
                    <p className="text-white text-2xl font-bold">{educationMetrics.overview?.totalLessonsCompleted || 0}</p>
                  </Card>
                  <Card className="p-5 bg-pink-900/30 border-pink-500/30">
                    <p className="text-pink-300 text-sm">Quiz réussis</p>
                    <p className="text-white text-2xl font-bold">{educationMetrics.overview?.totalQuizzesPassed || 0}</p>
                  </Card>
                </div>

                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Performance par quiz</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-gray-400 text-sm border-b border-gray-700">
                          <th className="text-left py-3 px-2">Module</th>
                          <th className="text-right py-3 px-2">Tentatives</th>
                          <th className="text-right py-3 px-2">Taux réussite</th>
                          <th className="text-right py-3 px-2">Score moyen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {educationMetrics.quizPerformance?.slice(0, 8).map((quiz, idx) => (
                          <tr key={idx} className="text-white border-b border-gray-700/50">
                            <td className="py-3 px-2 text-sm">{quiz.module}</td>
                            <td className="text-right py-3 px-2">{quiz.attempts}</td>
                            <td className="text-right py-3 px-2">
                              <span className={`px-2 py-1 rounded text-sm ${
                                quiz.passRate > 70 ? 'bg-green-500/20 text-green-400' :
                                quiz.passRate > 50 ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                                {quiz.passRate}%
                              </span>
                            </td>
                            <td className="text-right py-3 px-2">{quiz.avgScore}/100</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {educationMetrics.badgeDistribution && Object.keys(educationMetrics.badgeDistribution).length > 0 && (
                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Distribution des badges</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(educationMetrics.badgeDistribution).map(([badge, count]) => (
                        <div key={badge} className="p-3 bg-yellow-900/30 rounded-lg text-center">
                          <Award className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
                          <p className="text-white font-bold">{count}</p>
                          <p className="text-gray-400 text-xs">{badge.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}

            {/* Health Behavior */}
            {activeMetricTab === 'health' && healthBehavior && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Adoption du suivi santé</h4>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-pink-300">Suivi cycle</span>
                          <span className="text-white">{healthBehavior.trackingAdoption?.cycleTracking?.users || 0}</span>
                        </div>
                        <Progress value={healthBehavior.trackingAdoption?.cycleTracking?.adoptionRate || 0} className="h-2" />
                        <p className="text-gray-400 text-xs text-right mt-1">
                          {healthBehavior.trackingAdoption?.cycleTracking?.adoptionRate || 0}% adoption
                        </p>
                      </div>
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-purple-300">Suivi contraception</span>
                          <span className="text-white">{healthBehavior.trackingAdoption?.contraceptionTracking?.users || 0}</span>
                        </div>
                        <Progress value={healthBehavior.trackingAdoption?.contraceptionTracking?.adoptionRate || 0} className="h-2" />
                        <p className="text-gray-400 text-xs text-right mt-1">
                          {healthBehavior.trackingAdoption?.contraceptionTracking?.adoptionRate || 0}% adoption
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Efficacité des rappels</h4>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Total rappels</span>
                        <span className="text-white font-bold">{healthBehavior.reminderEffectiveness?.totalReminders || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Envoyés</span>
                        <span className="text-green-400 font-bold">{healthBehavior.reminderEffectiveness?.sentReminders || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Taux d'engagement</span>
                        <span className="text-blue-400 font-bold">{healthBehavior.reminderEffectiveness?.engagementRate || 0}%</span>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Tendances d'observance (3 mois)</h4>
                  <div className="flex items-end gap-4 h-40">
                    {healthBehavior.adherenceTrends?.map((month, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-purple-500 rounded-t"
                          style={{ height: `${Math.max((month.logs / 100) * 100, 10)}%` }}
                        />
                        <p className="text-gray-400 text-xs mt-2">{month.period}</p>
                        <p className="text-white text-sm font-bold">{month.logs}</p>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Utilisatrices consistantes</h4>
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center">
                      <span className="text-green-400 text-2xl font-bold">{healthBehavior.consistentTrackers?.count || 0}</span>
                    </div>
                    <div>
                      <p className="text-white">Streak de 7+ jours consécutifs</p>
                      <p className="text-gray-400 text-sm">
                        {healthBehavior.consistentTrackers?.rate || 0}% des utilisatrices de contraception
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Community Engagement */}
            {activeMetricTab === 'community' && communityMetrics && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Questions Communauté</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-purple-900/30 rounded-lg text-center">
                          <p className="text-purple-300 text-sm">Total</p>
                          <p className="text-white text-2xl font-bold">{communityMetrics.communityQuestions?.total || 0}</p>
                        </div>
                        <div className="p-4 bg-green-900/30 rounded-lg text-center">
                          <p className="text-green-300 text-sm">Approuvées</p>
                          <p className="text-white text-2xl font-bold">{communityMetrics.communityQuestions?.approved || 0}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Réponses totales</span>
                        <span className="text-white font-bold">{communityMetrics.communityQuestions?.totalAnswers || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Moy. réponses/question</span>
                        <span className="text-white font-bold">{communityMetrics.communityQuestions?.avgAnswersPerQuestion || 0}</span>
                      </div>
                    </div>

                    {communityMetrics.communityQuestions?.byCategory && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <p className="text-gray-400 text-sm mb-2">Par catégorie:</p>
                        <div className="space-y-2">
                          {Object.entries(communityMetrics.communityQuestions.byCategory).map(([cat, count]) => (
                            <div key={cat} className="flex items-center gap-2">
                              <span className="text-gray-300 text-sm flex-1">{cat}</span>
                              <span className="text-white">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>

                  <Card className="p-6 bg-gray-800 border-gray-700">
                    <h4 className="text-lg font-bold text-white mb-4">Programme Girls Room</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-pink-900/30 rounded-lg text-center">
                          <p className="text-pink-300 text-sm">Sessions</p>
                          <p className="text-white text-2xl font-bold">{communityMetrics.girlsRoomProgram?.totalEvents || 0}</p>
                        </div>
                        <div className="p-4 bg-pink-900/30 rounded-lg text-center">
                          <p className="text-pink-300 text-sm">Participations</p>
                          <p className="text-white text-2xl font-bold">{communityMetrics.girlsRoomProgram?.totalParticipations || 0}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Participantes uniques</span>
                        <span className="text-white font-bold">{communityMetrics.girlsRoomProgram?.uniqueParticipants || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Moy. par session</span>
                        <span className="text-white font-bold">{communityMetrics.girlsRoomProgram?.avgParticipantsPerEvent || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Badges Ambassadrice</span>
                        <span className="text-yellow-400 font-bold">{communityMetrics.girlsRoomProgram?.ambassadricesBadgeEarned || 0}</span>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="p-6 bg-gray-800 border-gray-700">
                  <h4 className="text-lg font-bold text-white mb-4">Croissance virale</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-900/30 rounded-lg text-center">
                      <p className="text-blue-300 text-sm">Utilisatrices qui ont partagé</p>
                      <p className="text-white text-2xl font-bold">{communityMetrics.viralGrowth?.usersWhoShared || 0}</p>
                    </div>
                    <div className="p-4 bg-blue-900/30 rounded-lg text-center">
                      <p className="text-blue-300 text-sm">Total partages</p>
                      <p className="text-white text-2xl font-bold">{communityMetrics.viralGrowth?.totalShares || 0}</p>
                    </div>
                    <div className="p-4 bg-blue-900/30 rounded-lg text-center">
                      <p className="text-blue-300 text-sm">Taux de partage</p>
                      <p className="text-white text-2xl font-bold">{communityMetrics.viralGrowth?.shareRate || 0}%</p>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* Loading state for metrics */}
            {!commercialSummary && !retentionMetrics && !geoMetrics && !educationMetrics && !healthBehavior && !communityMetrics && (
              <Card className="p-12 bg-gray-800 border-gray-700 text-center">
                <RefreshCw className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-spin" />
                <p className="text-gray-400">Chargement des métriques avancées...</p>
              </Card>
            )}
          </TabsContent>

          {/* Interoperability Hub Tab */}
          <TabsContent value="interoperability" className="space-y-6">
            <InteroperabilityHub />
          </TabsContent>
        </Tabs>
      </div>

      {/* Event Create/Edit Dialog */}
      <Dialog open={showEventDialog || !!editingEvent} onOpenChange={(open) => {
        if (!open) { setShowEventDialog(false); setEditingEvent(null); }
      }}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingEvent ? 'Modifier la session' : 'Nouvelle session Girls Room'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Date</Label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-300">Heure</Label>
                <Input
                  type="time"
                  value={eventForm.time}
                  onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Ville</Label>
              <Select value={eventForm.city} onValueChange={(v) => setEventForm({ ...eventForm, city: v })}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {CITIES.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Établissement</Label>
              <Input
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="Ex: Lycée Classique d'Abidjan"
                className="bg-gray-700 border-gray-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Thème</Label>
              <Input
                value={eventForm.theme}
                onChange={(e) => setEventForm({ ...eventForm, theme: e.target.value })}
                placeholder="Ex: Parler de ses règles sans honte"
                className="bg-gray-700 border-gray-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Animatrice</Label>
              <Input
                value={eventForm.animator}
                onChange={(e) => setEventForm({ ...eventForm, animator: e.target.value })}
                placeholder="Ex: Mme Koné"
                className="bg-gray-700 border-gray-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Description (optionnel)</Label>
              <Textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Description additionnelle..."
                className="bg-gray-700 border-gray-600 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEventDialog(false); setEditingEvent(null); }} className="border-gray-600 text-gray-300">
              Annuler
            </Button>
            <Button onClick={editingEvent ? handleUpdateEvent : handleCreateEvent} className="bg-pink-500 hover:bg-pink-600">
              {editingEvent ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Envoyer une notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Titre</Label>
              <Input
                value={notifyForm.title}
                onChange={(e) => setNotifyForm({ ...notifyForm, title: e.target.value })}
                placeholder="Ex: 🌸 Nouvelle session Girls Room!"
                className="bg-gray-700 border-gray-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Message</Label>
              <Textarea
                value={notifyForm.message}
                onChange={(e) => setNotifyForm({ ...notifyForm, message: e.target.value })}
                placeholder="Le contenu de la notification..."
                className="bg-gray-700 border-gray-600 text-white mt-1 min-h-[100px]"
              />
            </div>
            <div>
              <Label className="text-gray-300">Destinataires</Label>
              <Select value={notifyForm.city} onValueChange={(v) => setNotifyForm({ ...notifyForm, city: v })}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  <SelectItem value="all">Toutes les utilisatrices</SelectItem>
                  {CITIES.map(city => (
                    <SelectItem key={city} value={city}>Utilisatrices de {city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotifyDialog(false)} className="border-gray-600 text-gray-300">
              Annuler
            </Button>
            <Button onClick={handleSendNotification} className="bg-purple-600 hover:bg-purple-700">
              <Send className="w-4 h-4 mr-2" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
